import { useCallback, useEffect } from 'react';

import { BulkUserCreationData } from '../BulkUserCreation';
import { getRolesInput } from '../BulkUserCreation.helpers';

import { noop, Flex, Typography } from '@aircall/tractor-v2';
import { ExternalLink } from '@components/Link/ExternalLink';
import { OptimisticProgressBar } from '@components/OptimisticProgressBar/OptimisticProgressBar';
import { fileToBase64, FormWizard, Gap, useFormWizard, useGraphMutation } from '@dashboard/library';
import { RoleInput } from '@generated/globalTypes';
import {
  InviteMultipleUsersMutation,
  InviteMultipleUsersMutationVariables,
} from '@generated/InviteMultipleUsersMutation';
import { INVITE_MULTIPLE_USERS_MUTATION } from '@graphql/mutations/InviteMultipleUsersMutation';
import { getRoleFlags } from '@helpers/roles.helpers';
import { useCompanyRoles } from '@hooks/useCompanyRoles';
import { usePermissions } from '@hooks/usePermissions';
import { Trans, useTranslation } from 'react-i18next';

export function BulkUserCreationSendInvitations() {
  const { t } = useTranslation();
  const { navigateToNextPage, editData, data } = useFormWizard<BulkUserCreationData>();
  const [mutate] = useGraphMutation<
    InviteMultipleUsersMutation,
    InviteMultipleUsersMutationVariables
  >(INVITE_MULTIPLE_USERS_MUTATION);
  const { roles: userRoles } = usePermissions();
  const { companyRoles } = useCompanyRoles();
  const { file } = data;

  const sendInvitations = useCallback(
    async (roles: RoleInput[], f: File) => {
      const urlData = await fileToBase64(f);

      const res = await mutate({
        variables: { input: { roles, urlData } },
      });

      if (res.data) {
        const { createdCount, totalCount, error } = res.data.inviteMultipleUsers;
        editData({
          response: {
            createdCount,
            totalCount,
            error,
          },
        });
      } else {
        editData({
          response: null,
        });
      }

      navigateToNextPage();
    },
    // We do not include navigateToNextPage because its ref changes with page index
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editData, mutate]
  );

  useEffect(() => {
    // Safeguard against missing file but should never happen
    /* istanbul ignore next */
    if (!file) {
      return noop;
    }

    const sortedRoles = getRolesInput(getRoleFlags(userRoles), companyRoles);
    const roles = sortedRoles as RoleInput[];
    sendInvitations(roles, file);
    return undefined;
  }, [companyRoles, file, sendInvitations, userRoles]);

  return (
    <>
      <FormWizard.PageHeader>
        <FormWizard.PageHeaderTitle>
          {t('bulk_user_creation.send_invitations.title')}
        </FormWizard.PageHeaderTitle>
      </FormWizard.PageHeader>

      <FormWizard.PageContent>
        <Flex
          data-test='bulk-user-creation-send-invitations'
          flex='1'
          flexDirection='column'
          gap='l'
          justifyContent='center'
          alignItems='center'
          h={400}
        >
          <OptimisticProgressBar
            width='200px'
            height={4}
            color='primary.base'
            progress={0}
            speed={5}
            capping={90}
            maxCapping={90}
          />

          <Gap flexDirection='column' gap='xxs' alignItems='center'>
            <Typography variant='bodySemiboldM'>
              {t('bulk_user_creation.send_invitations.text')}
            </Typography>
            <Typography variant='bodyRegularS' color='neutral-400'>
              {t('bulk_user_creation.send_invitations.subtext')}
            </Typography>
          </Gap>
        </Flex>
      </FormWizard.PageContent>

      <FormWizard.PageSubText>
        <Trans i18nKey='bulk_user_creation.contact_support'>
          <ExternalLink href={t('bulk_user_creation.contact_support_link')} />
        </Trans>
      </FormWizard.PageSubText>
    </>
  );
}
